import { Search, CheckCircle, XCircle, Plus, CalendarDays, Pencil, Trash2, AlertTriangle, Link2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { AuditHistoryButton } from './AuditHistoryModal';
import WorkerCertificates from './WorkerCertificates';
import RegistratieLinkModal from './RegistratieLinkModal';
import ViesCheck from './ViesCheck';
import WorkerAccount from './WorkerAccount';
import Aanmeldingen from './Aanmeldingen';
import './AvailableWorkers.css';
import { fold } from '../../utils/zoek';

// Worst certificate status of a worker -> dot colour + tooltip.
const CERT_DOT = {
  red:    { cls: 'cert-dot-red',    title: 'Certificaat verlopen — niet inplanbaar' },
  yellow: { cls: 'cert-dot-yellow', title: 'Certificaat verloopt binnenkort' },
  green:  { cls: 'cert-dot-green',  title: 'Certificaten geldig' },
};

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, { ...options, headers, credentials: 'include' });
}

// Every field on the worker form must be filled — both when adding and editing —
// so nothing gets forgotten. [stateKey, label] pairs, used to build the error.
const REQUIRED_FIELDS = [
  ['firstName', 'Voornaam'],
  ['lastName', 'Achternaam'],
  ['phone', 'Telefoonnummer'],
  ['email', 'E-mailadres'],
  ['bedrijfsnaam', 'Bedrijfsnaam'],
  ['functiegroepId', 'Functiegroep'],
  ['kvkNumber', 'KvK'],
  ['btwNumber', 'BTW'],
  ['address', 'Adres'],
  ['postalCode', 'Postcode'],
  ['city', 'Plaats'],
];
const REQUIRED_FIELDS_EDIT = [
  ['firstName', 'Voornaam'],
  ['lastName', 'Achternaam'],
  ['phone', 'Telefoon'],
  ['email', 'E-mailadres'],
  ['bedrijfsnaam', 'Bedrijfsnaam'],
  ['functiegroepId', 'Functiegroep'],
  ['kvkNumber', 'KvK'],
  ['btwNumber', 'BTW'],
  ['address', 'Adres'],
  ['postalCode', 'Postcode'],
  ['city', 'Plaats'],
];

// Labels of required profile fields a worker is still missing (opdrachtgever is optional).
// functiegroepId comes back as 0 when unset, so treat 0/falsy as missing.
function getMissingFields(w) {
  return REQUIRED_FIELDS_EDIT
    .filter(([key]) => (key === 'functiegroepId' ? !w[key] : !String(w[key] ?? '').trim()))
    .map(([, label]) => label);
}

export default function AvailableWorkers({ workers, onWorkersChanged, onAanmeldingenCount, onAddWorker, onEditWorker, onDeleteWorker, onBulkAssignOpdrachtgever, onCertificatesChanged, assignments, clients = [], shiftTemplates = [], onSchedule }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [filterSkill, setFilterSkill] = useState('all');
  const [filterFunctiegroep, setFilterFunctiegroep] = useState('all'); // 'all' | 'none' | <id>

  // Bulk-select workers -> assign one opdrachtgever (bedrijf) to all of them.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOgId, setBulkOgId] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWorkerDetails, setSelectedWorkerDetails] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editError, setEditError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    bedrijfsnaam: '',
    functiegroepId: '',
    opdrachtgeverId: '',
    vog: false,
    kvkNumber: '',
    btwNumber: '',
    address: '',
    postalCode: '',
    city: '',
  });
  const [formError, setFormError] = useState('');
  const [functiegroepen, setFunctiegroepen] = useState([]);
  const [opdrachtgevers, setOpdrachtgevers] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [fgRes, ogRes] = await Promise.all([
          authFetch('/api/zzp/functiegroepen'),
          authFetch('/api/zzp/opdrachtgevers'),
        ]);
        const fg = await fgRes.json().catch(() => []);
        const og = await ogRes.json().catch(() => []);
        if (!cancelled) {
          setFunctiegroepen(Array.isArray(fg) ? fg : []);
          setOpdrachtgevers(Array.isArray(og) ? og : []);
        }
      } catch {
        if (!cancelled) { setFunctiegroepen([]); setOpdrachtgevers([]); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Multi-day scheduling state (inside worker details modal)
  const [schedDates, setSchedDates] = useState([]);
  const [schedClient, setSchedClient] = useState('');
  const [schedTemplateId, setSchedTemplateId] = useState('');
  const [schedTimeStart, setSchedTimeStart] = useState('08:00');
  const [schedTimeEnd, setSchedTimeEnd] = useState('16:00');
  const [schedSuccess, setSchedSuccess] = useState(false);
  const [schedError, setSchedError] = useState('');

  const getNextDays = (count) => {
    const days = [];
    const today = new Date();
    for (let i = 0; i < count; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      days.push(d.toISOString().split('T')[0]);
    }
    return days;
  };
  const next14 = getNextDays(14);

  const toggleSchedDate = (date) =>
    setSchedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );

  const handleMultiSchedule = async () => {
    if (!schedDates.length) { setSchedError('Selecteer minimaal één dag'); return; }
    if (!schedClient) { setSchedError('Selecteer een klant'); return; }
    // Catch the expired-certificate block up front, so we don't fire one request
    // (and one error) per selected day.
    if (selectedWorkerDetails.certStatus === 'red') {
      setSchedError(`${selectedWorkerDetails.name} heeft een verlopen certificaat en kan niet ingepland worden. Werk het certificaat bij onder "Certificaten".`);
      return;
    }
    setSchedError('');

    let start = schedTimeStart;
    let end = schedTimeEnd;
    if (schedTemplateId) {
      const t = shiftTemplates.find((t) => t.id === parseInt(schedTemplateId));
      if (t) { start = t.startTime; end = t.endTime; }
    }

    const results = [];
    for (const date of schedDates) {
      results.push(await onSchedule({
        workerId: selectedWorkerDetails.id,
        workerName: selectedWorkerDetails.name,
        skill: selectedWorkerDetails.skill,
        date,
        time: `${start} - ${end}`,
        client: schedClient,
      }));
    }
    // One combined message, never one per day.
    const failed = results.filter((r) => r && r.ok === false);
    if (failed.length) {
      setSchedError(failed[0].error + (failed.length > 1 ? ` (${failed.length} dagen niet ingepland)` : ''));
      return;
    }

    setSchedDates([]);
    setSchedClient('');
    setSchedTemplateId('');
    setSchedSuccess(true);
    setTimeout(() => setSchedSuccess(false), 3000);
  };

  // Functiegroep helpers (id -> naam, and worker counts per group).
  const fgName = (id) => {
    const f = functiegroepen.find((x) => String(x.id) === String(id));
    return f ? f.naam : '';
  };
  const fgCount = (id) => workers.filter((w) => String(w.functiegroepId) === String(id)).length;
  const noneCount = workers.filter((w) => !w.functiegroepId).length;

  const filteredWorkers = workers.filter((worker) => {
    const matchesSearch =
      fold(worker.name).includes(fold(searchTerm)) ||
      fold(worker.skill).includes(fold(searchTerm));
    const matchesSkill = filterSkill === 'all' || worker.skill === filterSkill;
    const matchesFg =
      filterFunctiegroep === 'all'
        ? true
        : filterFunctiegroep === 'none'
        ? !worker.functiegroepId
        : String(worker.functiegroepId) === String(filterFunctiegroep);
    return matchesSearch && matchesSkill && matchesFg;
  })
    // Alphabetical by surname (Dutch collation), falling back to first name.
    .sort((a, b) => {
      const ln = String(a.lastName || '').localeCompare(String(b.lastName || ''), 'nl', { sensitivity: 'base' });
      if (ln !== 0) return ln;
      return String(a.firstName || '').localeCompare(String(b.firstName || ''), 'nl', { sensitivity: 'base' });
    });

  const skills = [...new Set(workers.map((w) => w.skill))];
  const availableCount = workers.filter((w) => w.available).length;

  // ── Bulk selection ──
  const toggleSelect = (id) => {
    setBulkNote('');
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const visibleIds = filteredWorkers.map((w) => w.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const toggleSelectAll = () => {
    setBulkNote('');
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => { setSelectedIds(new Set()); setBulkOgId(''); setBulkNote(''); };

  const applyBulkOpdrachtgever = async () => {
    if (!selectedIds.size || !bulkOgId || bulkBusy) return;
    setBulkBusy(true); setBulkNote('');
    try {
      await onBulkAssignOpdrachtgever([...selectedIds], bulkOgId);
      const og = opdrachtgevers.find((o) => String(o.id) === String(bulkOgId));
      setBulkNote(`✓ ${og ? og.naam : 'Bedrijf'} toegepast op ${selectedIds.size} medewerker(s).`);
      setSelectedIds(new Set());
      setBulkOgId('');
    } catch (e) {
      setBulkNote('Fout: ' + (e?.message || 'toewijzen mislukt'));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormError('');
  };

  const handleAddWorker = (e) => {
    e.preventDefault();

    const missing = REQUIRED_FIELDS
      .filter(([key]) => !String(formData[key] ?? '').trim())
      .map(([, label]) => label);
    if (missing.length) {
      setFormError(`Alle velden zijn verplicht. Nog leeg: ${missing.join(', ')}`);
      return;
    }

    if (!/^\+?[0-9\s\-()]{10,}$/.test(formData.phone)) {
      setFormError('Voer een geldig telefoonnummer in');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email.trim())) {
      setFormError('Voer een geldig e-mailadres in');
      return;
    }

    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();

    onAddWorker({
      firstName,
      lastName,
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      bedrijfsnaam: formData.bedrijfsnaam.trim(),
      functiegroepId: formData.functiegroepId || null,
      opdrachtgeverId: formData.opdrachtgeverId || null,
      vog: !!formData.vog,
      kvkNumber: formData.kvkNumber.trim(),
      btwNumber: formData.btwNumber.trim(),
      address: formData.address.trim(),
      postalCode: formData.postalCode.trim(),
      city: formData.city.trim(),
      available: true,
    });

    setFormData({
      firstName: '', lastName: '', phone: '', email: '', bedrijfsnaam: '', functiegroepId: '', opdrachtgeverId: '', vog: false,
      kvkNumber: '', btwNumber: '', address: '', postalCode: '', city: '',
    });
    setIsModalOpen(false);
  };

  const getWorkerAssignments = (workerId) => {
    return (assignments || []).filter((a) => a.workerId === workerId);
  };

  const openWorkerDetails = (worker) => {
    setSelectedWorkerDetails(worker);
    setIsEditing(false);
    setEditError('');
    setSchedDates([]);
    setSchedClient('');
    setSchedTemplateId('');
    setSchedTimeStart('08:00');
    setSchedTimeEnd('16:00');
    setSchedSuccess(false);
    setSchedError('');
  };

  const startEditing = (worker) => {
    const w = worker || selectedWorkerDetails;
    setEditFormData({
      firstName: w.firstName || '',
      lastName: w.lastName || '',
      phone: w.phone || '',
      email: w.email || '',
      bedrijfsnaam: w.bedrijfsnaam || '',
      functiegroepId: w.functiegroepId || '',
      opdrachtgeverId: w.opdrachtgeverId || '',
      vog: !!w.vog,
      kvkNumber: w.kvkNumber || '',
      btwNumber: w.btwNumber || '',
      address: w.address || '',
      postalCode: w.postalCode || '',
      city: w.city || '',
    });
    setEditError('');
    setIsEditing(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    setEditError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const missing = REQUIRED_FIELDS_EDIT
      .filter(([key]) => !String(editFormData[key] ?? '').trim())
      .map(([, label]) => label);
    if (missing.length) {
      setEditError(`Alle velden zijn verplicht. Nog leeg: ${missing.join(', ')}`);
      return;
    }
    if (!/^\+?[0-9\s\-()]{10,}$/.test(editFormData.phone)) {
      setEditError('Voer een geldig telefoonnummer in');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(editFormData.email.trim())) {
      setEditError('Voer een geldig e-mailadres in');
      return;
    }
    await onEditWorker(selectedWorkerDetails.id, {
      ...editFormData,
      functiegroepId: editFormData.functiegroepId || null,
      opdrachtgeverId: editFormData.opdrachtgeverId || null,
      vog: !!editFormData.vog,
    });
    setIsEditing(false);
    setSelectedWorkerDetails(null);
  };

  return (
    <div className="available-workers">
      {linkOpen && <RegistratieLinkModal onClose={() => setLinkOpen(false)} />}
      <Aanmeldingen onAccepted={onWorkersChanged} onCountChange={onAanmeldingenCount} />
      <div className="workers-header">
        <div>
          <h2>Beschikbare Medewerkers</h2>
          <p>Bekijk en beheer je personeel</p>
        </div>
        <div className="workers-header-actions">
          <button className="add-worker-btn" onClick={() => setIsModalOpen(true)}>
            <Plus size={18} />
            Nieuwe Medewerker
          </button>
          <button className="reg-link-btn" onClick={() => setLinkOpen(true)}>
            <Link2 size={18} />
            Registratielink
          </button>
        </div>
        <div className="stats">
          <div className="stat-item">
            <span className="stat-number">{availableCount}</span>
            <span className="stat-label">Nu Beschikbaar</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{workers.length}</span>
            <span className="stat-label">Totaal Medewerkers</span>
          </div>
        </div>
      </div>

      <div className="workers-controls">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Zoeken op naam of vaardigheid..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          className="skill-filter"
          value={filterSkill}
          onChange={(e) => setFilterSkill(e.target.value)}
        >
          <option value="all">Alle Vaardigheden</option>
          {skills.map((skill) => (
            <option key={skill} value={skill}>
              {skill}
            </option>
          ))}
        </select>
      </div>

      {functiegroepen.length > 0 && (
        <div className="fg-filter-bar">
          <span className="fg-filter-label">Functiegroep:</span>
          <button
            type="button"
            className={`fg-chip ${filterFunctiegroep === 'all' ? 'active' : ''}`}
            onClick={() => setFilterFunctiegroep('all')}
          >
            Alle <span className="fg-chip-count">{workers.length}</span>
          </button>
          {functiegroepen.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`fg-chip ${String(filterFunctiegroep) === String(f.id) ? 'active' : ''}`}
              onClick={() => setFilterFunctiegroep(f.id)}
            >
              {f.naam} <span className="fg-chip-count">{fgCount(f.id)}</span>
            </button>
          ))}
          {noneCount > 0 && (
            <button
              type="button"
              className={`fg-chip ${filterFunctiegroep === 'none' ? 'active' : ''}`}
              onClick={() => setFilterFunctiegroep('none')}
            >
              Zonder functiegroep <span className="fg-chip-count">{noneCount}</span>
            </button>
          )}
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="wk-bulk-bar">
          <span className="wk-bulk-count">{selectedIds.size} geselecteerd</span>
          <span className="wk-bulk-label">Bedrijf toewijzen:</span>
          <select className="skill-filter" value={bulkOgId} onChange={(e) => setBulkOgId(e.target.value)} disabled={bulkBusy}>
            <option value="">Kies opdrachtgever…</option>
            {opdrachtgevers.map((o) => <option key={o.id} value={o.id}>{o.naam}</option>)}
          </select>
          <button className="wk-bulk-apply" onClick={applyBulkOpdrachtgever} disabled={!bulkOgId || bulkBusy}>
            {bulkBusy ? 'Bezig…' : 'Toepassen'}
          </button>
          <button className="wk-bulk-clear" onClick={clearSelection} disabled={bulkBusy}>Deselecteren</button>
        </div>
      )}
      {bulkNote && <div className="wk-bulk-note">{bulkNote}</div>}

      <div className="workers-table">
        {filteredWorkers.length > 0 ? (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th className="sel-col">
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} title="Alles selecteren" />
                  </th>
                  <th>Naam</th>
                  <th>Contact</th>
                  <th>Functiegroep</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredWorkers.map((worker) => (
                  <tr
                    key={worker.id}
                    className={`${!worker.available ? 'unavailable' : ''} ${selectedIds.has(worker.id) ? 'row-selected' : ''}`}
                    onClick={() => openWorkerDetails(worker)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="sel-col" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(worker.id)}
                        onChange={() => toggleSelect(worker.id)}
                      />
                    </td>
                    <td className="name-cell">
                      <div className="worker-name">
                        {CERT_DOT[worker.certStatus] && (
                          <span
                            className={`cert-dot ${CERT_DOT[worker.certStatus].cls}`}
                            title={CERT_DOT[worker.certStatus].title}
                          />
                        )}
                        {worker.name}
                        {worker.registratieNieuw && (
                          <span className="worker-new-badge" title="Zelf geregistreerd via link. Documenten staan bij Documenten.">Nieuw</span>
                        )}
                        {getMissingFields(worker).length > 0 && (
                          <span
                            className="worker-incomplete"
                            title={`Onvolledig profiel — ontbreekt nog: ${getMissingFields(worker).join(', ')}`}
                          >
                            <AlertTriangle size={15} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="contact-cell">{worker.phone}</td>
                    <td className="fg-cell">
                      {fgName(worker.functiegroepId)
                        ? <span className="fg-tag">{fgName(worker.functiegroepId)}</span>
                        : <span className="fg-tag fg-tag--none">—</span>}
                    </td>
                    <td>
                      <div className={`status ${worker.available ? 'available' : 'unavailable'}`}>
                        {worker.available ? (
                          <>
                            <CheckCircle size={16} />
                            <span>Beschikbaar</span>
                          </>
                        ) : (
                          <>
                            <XCircle size={16} />
                            <span>Niet beschikbaar</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="row-actions" onClick={(e) => e.stopPropagation()}>
                      <AuditHistoryButton entityType="worker" entityId={worker.id} label={worker.name} className="row-btn" iconOnly />
                      <button
                        className="row-btn row-btn--edit"
                        title="Bewerken"
                        onClick={() => { openWorkerDetails(worker); startEditing(worker); }}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="row-btn row-btn--delete"
                        title="Verwijderen"
                        onClick={() => { if (window.confirm(`${worker.name} verwijderen?`)) onDeleteWorker(worker.id); }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-results">
            <p>Geen medewerkers gevonden die overeenkomen met je zoekopdracht</p>
          </div>
        )}
      </div>

      <div className="workers-footer">
        <p>{filteredWorkers.length} van {workers.length} medewerkers getoond</p>
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nieuwe Medewerker</h3>
              <button
                className="modal-close"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddWorker} className="add-worker-form">
              {/* Compact two-column layout — all 11 fields without endless scrolling. */}
              <div className="awf-grid">
                <div className="form-group">
                  <label htmlFor="firstName">Voornaam</label>
                  <input id="firstName" type="text" name="firstName" placeholder="bijv. Jan Willem" autoComplete="off"
                    value={formData.firstName} onChange={handleFormChange} autoFocus />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Achternaam</label>
                  <input id="lastName" type="text" name="lastName" placeholder="bijv. van der Berg" autoComplete="off"
                    value={formData.lastName} onChange={handleFormChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Telefoonnummer</label>
                  <input id="phone" type="tel" name="phone" placeholder="+31 6 12345678"
                    value={formData.phone} onChange={handleFormChange} />
                </div>

                <div className="form-group">
                  <label htmlFor="email">E-mailadres</label>
                  <input id="email" type="email" name="email" placeholder="naam@bedrijf.nl"
                    value={formData.email} onChange={handleFormChange} />
                </div>
                <div className="form-group awf-span2">
                  <label htmlFor="bedrijfsnaam">Bedrijfsnaam</label>
                  <input id="bedrijfsnaam" type="text" name="bedrijfsnaam" placeholder="A.H. Sales B.V."
                    value={formData.bedrijfsnaam} onChange={handleFormChange} />
                </div>

                <div className="form-group">
                  <label htmlFor="functiegroepId" title="Bepaalt de tarieven voor ZZP-contracten">Functiegroep</label>
                  <select id="functiegroepId" name="functiegroepId" value={formData.functiegroepId || ''} onChange={handleFormChange}>
                    <option value="">— Geen functiegroep —</option>
                    {functiegroepen.map((f) => <option key={f.id} value={f.id}>{f.naam}</option>)}
                  </select>
                </div>
                <div className="form-group optional">
                  <label htmlFor="opdrachtgeverId" title="Bedrijf waar deze medewerker werkt">Bedrijf (opdrachtgever)</label>
                  <select id="opdrachtgeverId" name="opdrachtgeverId" value={formData.opdrachtgeverId || ''} onChange={handleFormChange}>
                    <option value="">— Geen opdrachtgever —</option>
                    {opdrachtgevers.map((o) => <option key={o.id} value={o.id}>{o.naam}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="kvkNumber">KvK</label>
                  <input id="kvkNumber" type="text" name="kvkNumber" placeholder="12345678"
                    value={formData.kvkNumber} onChange={handleFormChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="btwNumber">BTW</label>
                  <input id="btwNumber" type="text" name="btwNumber" placeholder="NL000000000B00"
                    value={formData.btwNumber} onChange={handleFormChange} />
                  <ViesCheck btw={formData.btwNumber} bedrijfsnaam={formData.bedrijfsnaam} />
                </div>

                <div className="form-group awf-span2">
                  <label htmlFor="address">Adres</label>
                  <input id="address" type="text" name="address" placeholder="Straatnaam 12"
                    value={formData.address} onChange={handleFormChange} />
                </div>

                <div className="form-group">
                  <label htmlFor="postalCode">Postcode</label>
                  <input id="postalCode" type="text" name="postalCode" placeholder="1234 AB"
                    value={formData.postalCode} onChange={handleFormChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="city">Plaats</label>
                  <input id="city" type="text" name="city" placeholder="Rotterdam"
                    value={formData.city} onChange={handleFormChange} />
                </div>

                <label className="vog-check awf-span2">
                  <input type="checkbox" checked={!!formData.vog}
                    onChange={(e) => setFormData((f) => ({ ...f, vog: e.target.checked }))} />
                  <span>VOG aanwezig <em>(optioneel)</em></span>
                </label>
              </div>

              {formError && <div className="form-error">{formError}</div>}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setIsModalOpen(false)}
                >
                  Annuleren
                </button>
                <button type="submit" className="btn-submit">
                  <Plus size={16} />
                  Nieuwe Medewerker
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedWorkerDetails && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedWorkerDetails(null)}
        >
          <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedWorkerDetails.name}</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {!isEditing && (
                  <button className="edit-worker-btn" onClick={() => startEditing()} title="Medewerker bewerken">
                    <Pencil size={16} />
                    Bewerken
                  </button>
                )}
                <button
                  className="modal-close"
                  onClick={() => setSelectedWorkerDetails(null)}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="worker-details">
              {isEditing ? (
                <form className="add-worker-form" onSubmit={handleEditSubmit} style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="form-group">
                      <label>Voornaam</label>
                      <input name="firstName" value={editFormData.firstName} onChange={handleEditChange} placeholder="Voornaam" />
                    </div>
                    <div className="form-group">
                      <label>Achternaam</label>
                      <input name="lastName" value={editFormData.lastName} onChange={handleEditChange} placeholder="Achternaam" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Telefoon</label>
                    <input name="phone" value={editFormData.phone || ''} onChange={handleEditChange} placeholder="+31 6 12345678" />
                  </div>
                  <div className="form-group">
                    <label>E-mailadres</label>
                    <input name="email" type="email" value={editFormData.email || ''} onChange={handleEditChange} placeholder="naam@bedrijf.nl" />
                  </div>
                  <div className="form-group">
                    <label>Bedrijfsnaam</label>
                    <input name="bedrijfsnaam" value={editFormData.bedrijfsnaam || ''} onChange={handleEditChange} placeholder="A.H. Sales B.V." />
                  </div>
                  <div className="form-group">
                    <label>Functiegroep (bepaalt tarieven voor ZZP-contracten)</label>
                    <select name="functiegroepId" value={editFormData.functiegroepId || ''} onChange={handleEditChange}>
                      <option value="">— Geen functiegroep —</option>
                      {functiegroepen.map((f) => <option key={f.id} value={f.id}>{f.naam}</option>)}
                    </select>
                  </div>
                  <div className="form-group optional">
                    <label>Bedrijf (opdrachtgever waar ze werken)</label>
                    <select name="opdrachtgeverId" value={editFormData.opdrachtgeverId || ''} onChange={handleEditChange}>
                      <option value="">— Geen opdrachtgever —</option>
                      {opdrachtgevers.map((o) => <option key={o.id} value={o.id}>{o.naam}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="form-group">
                      <label>KvK</label>
                      <input name="kvkNumber" value={editFormData.kvkNumber || ''} onChange={handleEditChange} placeholder="12345678" />
                    </div>
                    <div className="form-group">
                      <label>BTW</label>
                      <input name="btwNumber" value={editFormData.btwNumber || ''} onChange={handleEditChange} placeholder="NL000000000B00" />
                      <ViesCheck btw={editFormData.btwNumber} bedrijfsnaam={editFormData.bedrijfsnaam} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Adres</label>
                    <input name="address" value={editFormData.address || ''} onChange={handleEditChange} placeholder="Straatnaam 12" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
                    <div className="form-group">
                      <label>Postcode</label>
                      <input name="postalCode" value={editFormData.postalCode || ''} onChange={handleEditChange} placeholder="1234 AB" />
                    </div>
                    <div className="form-group">
                      <label>Plaats</label>
                      <input name="city" value={editFormData.city || ''} onChange={handleEditChange} placeholder="Rotterdam" />
                    </div>
                  </div>

                  <label className="vog-check">
                    <input type="checkbox" checked={!!editFormData.vog}
                      onChange={(e) => setEditFormData((f) => ({ ...f, vog: e.target.checked }))} />
                    <span>VOG aanwezig <em>(optioneel)</em></span>
                  </label>

                  {editError && <div className="form-error">{editError}</div>}
                  <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={() => setIsEditing(false)}>Annuleren</button>
                    <button type="submit" className="btn-submit">Wijzigingen Opslaan</button>
                  </div>
                </form>
              ) : (
              <div className="worker-info-card">
                <div className="info-row">
                  <span className="info-label">Vaardigheid</span>
                  <span className="info-value skill">{selectedWorkerDetails.skill}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Telefoon</span>
                  <span className="info-value">{selectedWorkerDetails.phone}</span>
                </div>
                {selectedWorkerDetails.email && (
                <div className="info-row">
                  <span className="info-label">E-mailadres</span>
                  <span className="info-value">{selectedWorkerDetails.email}</span>
                </div>
                )}
                {selectedWorkerDetails.opdrachtgeverName && (
                <div className="info-row">
                  <span className="info-label">Bedrijf</span>
                  <span className="info-value">{selectedWorkerDetails.opdrachtgeverName}</span>
                </div>
                )}
                <div className="info-row">
                  <span className="info-label">Status</span>
                  <span
                    className={`status-badge ${
                      selectedWorkerDetails.available ? 'available' : 'unavailable'
                    }`}
                  >
                    {selectedWorkerDetails.available ? 'Beschikbaar' : 'Niet beschikbaar'}
                  </span>
                </div>
              </div>
              )}

              {!isEditing && (
                <>
                  <WorkerCertificates workerId={selectedWorkerDetails.id} onChanged={onCertificatesChanged} />
                  <WorkerAccount workerId={selectedWorkerDetails.id} />
                </>
              )}

              {/* Multi-day scheduling */}
              <div className="worker-schedule-panel">
                <h4>
                  <CalendarDays size={16} />
                  Meerdere Dagen Inplannen
                </h4>

                <div className="sched-date-quick">
                  <button type="button" onClick={() => setSchedDates(getNextDays(7))}>Deze week</button>
                  <button type="button" onClick={() => setSchedDates(next14)}>Komende 2 weken</button>
                  <button type="button" className="sched-clear-btn" onClick={() => setSchedDates([])}>Wissen</button>
                </div>

                <div className="sched-date-grid">
                  {next14.map((dateStr) => {
                    const d = new Date(dateStr);
                    const isWeekend = [0, 6].includes(d.getDay());
                    const isSelected = schedDates.includes(dateStr);
                    return (
                      <label
                        key={dateStr}
                        className={`sched-date-chip ${isSelected ? 'selected' : ''} ${isWeekend ? 'weekend' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSchedDate(dateStr)}
                        />
                        <strong>{d.toLocaleDateString('nl-NL', { weekday: 'short' })}</strong>
                        <span>{d.getDate()}/{d.getMonth() + 1}</span>
                      </label>
                    );
                  })}
                </div>

                <div className="sched-options">
                  <div className="sched-field">
                    <label>Klant</label>
                    <select value={schedClient} onChange={(e) => setSchedClient(e.target.value)}>
                      <option value="">Selecteer klant...</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sched-field">
                    <label>Diensttijd</label>
                    {shiftTemplates.length > 0 && (
                      <select value={schedTemplateId} onChange={(e) => setSchedTemplateId(e.target.value)}>
                        <option value="">Aangepaste tijd...</option>
                        {shiftTemplates.map((t) => (
                          <option key={t.id} value={t.id}>{t.name} ({t.startTime}–{t.endTime})</option>
                        ))}
                      </select>
                    )}
                    {!schedTemplateId && (
                      <div className="sched-time-row">
                        <input type="time" value={schedTimeStart} onChange={(e) => setSchedTimeStart(e.target.value)} />
                        <span>–</span>
                        <input type="time" value={schedTimeEnd} onChange={(e) => setSchedTimeEnd(e.target.value)} />
                      </div>
                    )}
                  </div>
                </div>

                {schedError && <div className="sched-error">{schedError}</div>}
                {schedSuccess && <div className="sched-success">✓ {schedDates.length > 0 ? 'Toewijzingen' : 'Toewijzing'} aangemaakt!</div>}

                <button
                  className="sched-submit-btn"
                  onClick={handleMultiSchedule}
                  disabled={!schedDates.length || !schedClient}
                >
                  <Plus size={16} />
                  {schedDates.length > 1
                    ? `${schedDates.length} dagen inplannen`
                    : schedDates.length === 1
                    ? '1 dag inplannen'
                    : 'Inplannen'}
                </button>
              </div>

              <div className="scheduled-assignments">
                <h4>Ingeplande Toewijzingen</h4>
                {getWorkerAssignments(selectedWorkerDetails.id).length > 0 ? (
                  <div className="assignments-list">
                    {getWorkerAssignments(selectedWorkerDetails.id).map(
                      (assignment) => (
                        <div
                          key={assignment.id}
                          className={`assignment-card status-${assignment.status}`}
                        >
                          <div className="assignment-date">
                            {new Date(assignment.date).toLocaleDateString('nl-NL', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </div>
                          <div className="assignment-time">{assignment.time}</div>
                          <div className="assignment-client">{assignment.client}</div>
                          <div
                            className={`assignment-status status-${assignment.status}`}
                          >
                            {assignment.status}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <div className="no-assignments">
                    <p>Geen toewijzingen ingepland</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
